import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChatBanner, ChatHeader, ChatInput } from '../kuroco/ChatChrome';
import { AiBubble, ChatMenu, DateDivider, UserBubble } from '../kuroco/ChatPrimitives';
import {
  createChatSession,
  fetchChatHealth,
  fetchChatSession,
  postChatMessage,
  postChatMessageStream,
} from '../../api/dashboardApi';
import { confirmProposedActions, fetchActionCheckins, fetchGoals, saveActionCheckin } from '../../api/goalsApi';
import { dayLabel, tokyoToday, weekday } from '../../utils/tokyoDate';
import { CHECKIN_MEMO_MAX } from '../../constants/checkinReasons';
import { useCanEditStylist } from '../../hooks/useUserRole';
import { mapApiSessionDetail } from '../../adapters/kurocoAiData';
import { useAuth } from '../../auth/AuthContext';
import { useDashboard } from '../../context/DashboardContext';
import { fetchMyIdentity } from '../../api/authApi';
import { greetingFor } from '../../utils/callName';
import { notifyGoalsUpdated, useGoalsConfig } from '../../utils/goalsLocalStore';
import { buildPracticeMemoCard, mergeImproveChoices } from '../../utils/practiceMemo';
import { MAX_PLAN_ACTIONS, formatPlanLine, mergePlanIntoKpiSummary } from '../../utils/kpiSnapshot';
import { actionsRequestText, goalsActionTargetKeys } from '../../utils/actionTargets';
import PracticeMemoCard from './PracticeMemoCard';
import MonthVsTargetCard from './MonthVsTargetCard';
import ActionPlanCard from './ActionPlanCard';
import ReplyFeedback from './ReplyFeedback';

const STREAM_TIMEOUT_MS = 130_000;
const CIRCLED = ['①', '②', '③', '④', '⑤'];
const REVIEW_PREFIX = '【振り返り】';
const IMPROVE_PREFIX = '【改善相談】';

/** Which flow an assistant bubble answers — from the preceding user message. */
function flowOf(messages, idx) {
  const prev = messages[idx - 1];
  if (prev?.role !== 'user') return null;
  const text = prev.text || '';
  if (text.startsWith(REVIEW_PREFIX) || text.includes('現状分析') || text.includes('振り返り') || text.includes('当月実績')) {
    return 'review';
  }
  if (text.startsWith(IMPROVE_PREFIX) || text.includes('改善策') || text === '💡 改善策を相談') {
    return 'improve';
  }
  return null;
}

function hasRecordSignal(a) {
  if (a?.doneCount != null && Number(a.doneCount) > 0) return true;
  const r = String(a?.rate ?? '').trim();
  if (!r || r === '—') return false;
  return /^\d+(\.\d+)?%$/.test(r) || /回/.test(r);
}

function formatCheckinEntry(e) {
  if (e?.status === 'done') return `「${e.actionName}」できた`;
  const reason = e?.reason ? `（理由: ${e.reason}）` : '';
  return `「${e.actionName}」できなかった${reason}`;
}

/** Menu taps: review/improve run in-chat via AI; record stays in this same thread. */
function buildFlowPrompt(route, actions = [], latestCheckin = null, goals = null) {
  if (route === 'review') return `${REVIEW_PREFIX}今月の現状分析と振り返りをお願いします。`;
  if (route === 'improve') {
    // Same request as GoalEdit: one action per 指標 (追加KGI first), 1〜3 with a single 指標.
    // It used to say 「少なくとも3つ」, which overrode the prompt's per-指標 count rule.
    const lines = [actionsRequestText(goalsActionTargetKeys(goals), goals?.subKgiKey)];
    if (actions.some(hasRecordSignal)) {
      const rates = actions
        .map((a, i) => `${CIRCLED[i] || `${i + 1}.`}「${a.name}」${a.rate}`)
        .join(' / ');
      lines.push(`行動の実施率: ${rates}`);
    }
    if (latestCheckin?.entries?.length) {
      const body = latestCheckin.entries.map(formatCheckinEntry).join(' / ');
      lines.push(`直近の行動記録(${latestCheckin.date}): ${body}`);
    }
    return lines.join('\n');
  }
  return null;
}

function isRecordIntent(text) {
  const t = (text || '').trim();
  return t === '行動を記録する' || t.includes('行動を記録') || t === '📝 行動を記録する';
}

/** User confirmed the full 2–3 action proposal (改善相談). */
function isProposalConfirm(text) {
  const t = (text || '').trim();
  return (
    t.includes('これでやってみる') ||
    t.includes('こちらでやってみる') ||
    t === 'やってみる'
  );
}

/** User confirmed a single-action swap proposed in free-form chat. */
function isReplaceConfirm(text) {
  const t = (text || '').trim();
  return t.includes('これに差し替える') || t.includes('差し替える');
}

/** User confirmed GOAL_PROPOSAL (KGI / dual focus) from 【目標相談】or free chat. */
function isGoalsConfirm(text) {
  const t = (text || '').trim();
  return t.includes('この目標で進める') || t.includes('この目標で保存');
}

/** Sub-field labels — never treat as an action title (filter Goals / record lists). */
const ACTION_META_LABELS = new Set([
  'いつ',
  '何を',
  'なにを',
  'どのくらい',
  'どのぐらい',
  '頻度',
  'when',
  'what',
  'how',
  'how much',
  'how often',
]);

function isMetaActionLabel(name) {
  const n = String(name || '').trim().toLowerCase();
  if (!n) return true;
  if (ACTION_META_LABELS.has(n) || ACTION_META_LABELS.has(String(name || '').trim())) return true;
  return /^(選択肢|CHOICES|売上|客数)$/i.test(n);
}

function goalsActionsForRecord(goals) {
  const list = goals?.actions;
  if (!Array.isArray(list)) return [];
  return list
    .filter((a) => a?.name && !isMetaActionLabel(a.name))
    .slice(0, MAX_PLAN_ACTIONS)
    .map((a, i) => ({
      id: a.id ?? i + 1,
      name: a.name,
      focusKey: a.focusKey || null,
      cadence: a.cadence || '毎日',
      rate: a.rate ?? null,
      tone: a.tone || 'mid',
    }));
}

/** Current plan titles for the model — it must name exact titles in ACTION_REPLACE. */
function buildPlanLine(goals) {
  const list = goalsActionsForRecord(goals);
  return list.length ? formatPlanLine(list) : '';
}

function isCheckinChoices(choices) {
  return (
    choices.length === 2 &&
    choices.some((c) => c.includes('できた') && !c.includes('できなかった')) &&
    choices.some((c) => c.includes('できなかった'))
  );
}

/** AI-driven quick replies under the last assistant bubble. */
function ChoiceBar({ choices, onPick, disabled }) {
  if (!choices?.length) return null;
  if (isCheckinChoices(choices)) {
    const yes = choices.find((c) => !c.includes('できなかった'));
    const no = choices.find((c) => c.includes('できなかった'));
    return (
      <div className="checkin">
        <button type="button" className="checkin-btn" disabled={disabled} onClick={() => onPick(yes)}>
          <span className="checkin-btn-ic">✅</span>
          <span className="checkin-btn-l">{yes.replace(/^✅\s*/, '')}</span>
        </button>
        <button type="button" className="checkin-btn" disabled={disabled} onClick={() => onPick(no)}>
          <span className="checkin-btn-ic">✕</span>
          <span className="checkin-btn-l">{no.replace(/^✕\s*/, '')}</span>
        </button>
      </div>
    );
  }
  return (
    <div className="chips">
      {choices.map((c) => (
        <button type="button" key={c} className="chip" disabled={disabled} onClick={() => onPick(c)}>
          {c}
        </button>
      ))}
    </div>
  );
}

function clearLastChoices(messages) {
  const next = [...messages];
  for (let i = next.length - 1; i >= 0; i -= 1) {
    if (next[i].role === 'ai' && next[i].choices?.length) {
      next[i] = { ...next[i], choices: undefined };
      break;
    }
  }
  return next;
}

function buildEntries(actions, ans, reasons, memos = []) {
  return actions.map((a, i) => ({
    actionId: a.id ?? i + 1,
    actionName: a.name,
    status: ans[i] === 'できた' ? 'done' : 'miss',
    reason: ans[i] === 'できなかった' ? reasons[i] || null : null,
    memo: ans[i] === 'できなかった' ? (memos[i] || '').trim() || null : null,
  }));
}

/** Inline 行動記録 — same chat thread, no separate screen. */
function InlineRecordFlow({
  stylistName,
  shortDate,
  actions,
  reasonOpts,
  store,
  stylist,
  onFinished,
  onMemoWaiting,
}) {
  const canEditGoals = useCanEditStylist(stylist); // own record only — see goals_service.can_write_stylist_goals
  const [ans, setAns] = useState(() => actions.map(() => null));
  const [reasons, setReasons] = useState(() => actions.map(() => null));
  const [memos, setMemos] = useState(() => actions.map(() => ''));
  const [memoDone, setMemoDone] = useState(() => actions.map(() => false)); // ひと言 step closed (text or not)
  const [saveState, setSaveState] = useState('idle');
  const [saveError, setSaveError] = useState(null);
  const savedKeyRef = useRef(null);
  // Latest actions / onFinished without making them effect dependencies: the parent
  // rebuilds `actions` on every render (goals refresh after a save), and having it in the
  // deps re-ran the save effect each time → ~2 PUT/s, 58 PUTs in 35 s (2026-10-04 logs).
  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  const cleared = (i) => ans[i] === 'できた' || (ans[i] === 'できなかった' && reasons[i] && memoDone[i]);
  const done = actions.length > 0 && actions.every((_, i) => cleared(i));

  const setAnswer = (i, v) => {
    setAns((a) => {
      const n = [...a];
      n[i] = v;
      return n;
    });
    if (v === 'できた') {
      setReasons((r) => {
        const n = [...r];
        n[i] = null;
        return n;
      });
    }
  };
  const setReason = (i, v) => {
    setReasons((r) => {
      const n = [...r];
      n[i] = v;
      return n;
    });
  };
  const setMemo = (i, v) => setMemos((m) => { const n = [...m]; n[i] = v; return n; });
  const closeMemo = (i) => setMemoDone((d) => { const n = [...d]; n[i] = true; return n; });

  // ひと言 is typed in the chat's own input bar (自由入力), not a field in the thread (owner
  // 2026-10-26). While an action waits for one, the parent routes the next sent text here.
  const memoWaitIndex = actions.findIndex((_, i) => ans[i] === 'できなかった' && reasons[i] && !memoDone[i]);
  const onMemoWaitingRef = useRef(onMemoWaiting);
  onMemoWaitingRef.current = onMemoWaiting;
  useEffect(() => {
    const i = memoWaitIndex;
    onMemoWaitingRef.current?.(
      i < 0
        ? null
        : (text) => {
            setMemo(i, String(text || '').trim().slice(0, CHECKIN_MEMO_MAX));
            closeMemo(i);
          }
    );
    return () => onMemoWaitingRef.current?.(null);
  }, [memoWaitIndex]);

  useEffect(() => {
    if (!done || !store || !stylist || !canEditGoals) return;
    const key = JSON.stringify({ store, stylist, ans, reasons, memos });
    if (savedKeyRef.current === key) return;
    // Mark in-flight BEFORE the request: a re-render while it is pending must not send
    // the same answers again. Cleared on failure so 再試行 can send once more.
    savedKeyRef.current = key;
    const actionsNow = actionsRef.current;
    let cancelled = false;
    (async () => {
      setSaveState('saving');
      setSaveError(null);
      try {
        await saveActionCheckin({
          store,
          stylist,
          entries: buildEntries(actionsNow, ans, reasons, memos),
          source: 'ai_chat_inline',
        });
        // Pull refreshed actions[].rate into Goals tab
        try {
          const goalsRes = await fetchGoals({ store, stylist });
          if (goalsRes?.goals) notifyGoalsUpdated(goalsRes.goals);
        } catch {
          /* check-in already saved; Goals may lag until next load */
        }
        if (!cancelled) {
          setSaveState('saved');
          onFinishedRef.current?.({ ans, reasons, anyNo: ans.includes('できなかった'), actions: actionsNow });
        }
      } catch (err) {
        if (savedKeyRef.current === key) savedKeyRef.current = null;
        if (!cancelled) {
          setSaveState('error');
          setSaveError(err.message || '保存に失敗しました');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [done, store, stylist, ans, reasons, memos, canEditGoals]);

  return (
    <>
      <AiBubble
        html={`${stylistName}さん、<strong>${shortDate}</strong>の行動を振り返りましょう。<br><strong>できた／できなかった</strong> をタップするだけ。`}
      />
      {actions.map((action, index) => {
        if (index > 0 && !cleared(index - 1)) return null;
        const a = ans[index];
        return (
          <React.Fragment key={action.id ?? index}>
            <AiBubble
              html={`<strong>${CIRCLED[index] || index + 1}「${action.name}」</strong><br>${action.cadence || ''} —— できた？できなかった？`}
            />
            {!a && (
              <div className="checkin">
                <button type="button" className="checkin-btn" onClick={() => setAnswer(index, 'できた')}>
                  <span className="checkin-btn-ic">✅</span>
                  <span className="checkin-btn-l">できた</span>
                </button>
                <button type="button" className="checkin-btn" onClick={() => setAnswer(index, 'できなかった')}>
                  <span className="checkin-btn-ic">✕</span>
                  <span className="checkin-btn-l">できなかった</span>
                </button>
              </div>
            )}
            {a && <UserBubble text={a} />}
            {a === 'できなかった' && !reasons[index] && (
              <>
                <AiBubble html="そうだったんですね。差し支えなければ、<strong>できなかった理由</strong>を教えてください。" />
                <div className="chips">
                  {(reasonOpts || []).map((r) => (
                    <button type="button" key={r.label} className="chip" onClick={() => setReason(index, r.label)}>
                      {r.label}
                    </button>
                  ))}
                </div>
              </>
            )}
            {a === 'できなかった' && reasons[index] && <UserBubble text={reasons[index]} />}
            {a === 'できなかった' && reasons[index] && !memoDone[index] && (
              <>
                <AiBubble html="ひと言あれば、下の入力欄から送ってください（<strong>任意</strong>です）。" />
                <div className="chips">
                  <button type="button" className="chip" onClick={() => closeMemo(index)}>
                    なしで次へ
                  </button>
                </div>
              </>
            )}
            {a === 'できなかった' && memoDone[index] && (memos[index] || '').trim() && <UserBubble text={memos[index].trim()} />}
          </React.Fragment>
        );
      })}
      {done && (
        <div className="recap">
          <div className="recap-head">
            {saveState === 'saving' && '⏳ 保存中…'}
            {saveState === 'saved' && '✅ 記録しました'}
            {saveState === 'error' && `⚠ ${saveError || '保存に失敗しました'}`}
            {saveState === 'idle' && '✅ 記録しました'}
          </div>
          {actions.map((action, i) => (
            <div className="recap-row" key={action.id ?? i}>
              <span className="name">
                <span className="num">{action.id ?? i + 1}</span>
                {action.name}
              </span>
              <span className={ans[i] === 'できなかった' ? 'ans-no' : 'ans-yes'}>{ans[i]}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default function AiHome({
  data,
  onHistory,
  settingsPath,
  store,
  stylist,
  month,
  kpiSummary,
  dossierMeta,
  onSessionsChanged,
  sessionMatchesSelection = true,
  activeSessionStore = null,
  activeSessionStylist = null,
  activeSessionId,
  initialFlow,
  setActiveSessionId,
  startNewChat,
  chatMessages,
  setChatMessages,
  chatSending,
  setChatSending,
  chatError,
  setChatError,
  chatFailedText,
  setChatFailedText,
}) {
  const { user } = useAuth();
  const { myProfile, setMyProfile } = useDashboard();
  const [goals] = useGoalsConfig(user?.uid, store, stylist);
  const goalsRef = useRef(goals);
  goalsRef.current = goals;
  const abortRef = useRef(null);
  const hydratedSessionRef = useRef(null);
  const activeSessionIdRef = useRef(activeSessionId);
  activeSessionIdRef.current = activeSessionId;
  const createInFlightRef = useRef(null);
  const [healthWarn, setHealthWarn] = useState(null);
  const [recordOpen, setRecordOpen] = useState(false);
  // set by InlineRecordFlow while an action waits for its ひと言: the next sent text goes there
  const [memoCapture, setMemoCapture] = useState(null);
  const onMemoWaiting = useCallback((fn) => setMemoCapture(() => fn), []);
  /** Optimistic seed after confirm API — Goals is source of truth after notify. */
  const [decidedActions, setDecidedActions] = useState(null);
  const recordFinishedRef = useRef(false);
  /** Active structured flow: 'improve' shows 改善相談 banner + 実践メモ cards. */
  const [flow, setFlow] = useState(initialFlow === 'improve' ? 'improve' : null);
  const improveBootRef = useRef(false);
  // store/stylist switched while this thread is still about someone else — hold
  // the message instead of sending it into whichever person's numbers it lands on
  const [mismatchText, setMismatchText] = useState(null);
  const pendingAfterNewChatRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const h = await fetchChatHealth();
        if (cancelled) return;
        if (!h?.ok) {
          setHealthWarn('AI接続の設定が不完全です。バックエンドの Vertex / セッション設定を確認してください。');
        } else {
          setHealthWarn(null);
        }
      } catch {
        if (!cancelled) {
          setHealthWarn('AIサーバーに接続できません。localhost:8000 が起動しているか確認してください。');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadSession = useCallback(
    async (sessionId) => {
      if (!sessionId) return;
      try {
        const res = await fetchChatSession(sessionId);
        const mapped = mapApiSessionDetail(res, data.period.today);
        setChatMessages(
          (mapped.messages || []).map((m) => ({
            role: m.role,
            text: m.text,
            choices: m.choices || [],
            rateable: m.role === 'ai',
          }))
        );
        setChatError(null);
        hydratedSessionRef.current = sessionId;
      } catch (err) {
        setChatError(err.message || 'セッションの読み込みに失敗しました');
      }
    },
    [data.period.today, setChatMessages, setChatError]
  );

  useEffect(() => {
    if (!activeSessionId) {
      hydratedSessionRef.current = null;
      return;
    }
    // Already showing this session's messages
    if (hydratedSessionRef.current === activeSessionId && chatMessages.length > 0) return;
    // Just created locally — keep streaming/new messages, don't refetch
    if (chatMessages.length > 0 && hydratedSessionRef.current == null) {
      hydratedSessionRef.current = activeSessionId;
      return;
    }
    // Continue from history (or empty thread with known session id) — load from API
    loadSession(activeSessionId);
  }, [activeSessionId, chatMessages.length, loadSession]);

  // startNewChat / 新しいチャット clears session+messages in AiStack — close inline record too
  useEffect(() => {
    if (!activeSessionId && chatMessages.length === 0) {
      setRecordOpen(false);
      recordFinishedRef.current = false;
      setDecidedActions(null);
      setFlow(null);
      improveBootRef.current = false;
    }
  }, [activeSessionId, chatMessages.length]);

  const ensureSession = useCallback(async () => {
    if (activeSessionIdRef.current) return activeSessionIdRef.current;
    if (createInFlightRef.current) return createInFlightRef.current;
    if (!store || !stylist) {
      throw new Error('ホームで店舗とスタイリストを選択してから行動を記録してください');
    }
    createInFlightRef.current = (async () => {
      try {
        const created = await createChatSession({
          store,
          stylist,
          month,
          kpi_summary:
            mergePlanIntoKpiSummary(kpiSummary, buildPlanLine(goalsRef.current)) || undefined,
        });
        const sid = created.session_id;
        setActiveSessionId?.(sid);
        hydratedSessionRef.current = sid;
        activeSessionIdRef.current = sid;
        onSessionsChanged?.();
        return sid;
      } finally {
        createInFlightRef.current = null;
      }
    })();
    return createInFlightRef.current;
  }, [store, stylist, month, kpiSummary, setActiveSessionId, onSessionsChanged]);

  /** Server confirm: session.pending_proposal → Goals 毎日の行動プラン */
  const confirmActionsFromSession = useCallback(
    async (kind = 'full') => {
      let sid = activeSessionIdRef.current;
      if (!sid) sid = await ensureSession();
      if (!sid) throw new Error('セッションがありません。改善相談を先に実行してください');
      const res = await confirmProposedActions({ sessionId: sid, kind });
      if (res?.goals) notifyGoalsUpdated(res.goals);
      // 行動記録 runs against the live (approved) plan only. A confirmed 行動 is a
      // 確認中 submission (res.submission) until a manager approves — never seeded here.
      const next = goalsActionsForRecord(res?.goals).filter(
        (a) => a?.name && !isMetaActionLabel(a.name)
      );
      if (next.length) setDecidedActions(next);
      return res;
    },
    [ensureSession]
  );

  const openRecordFlow = useCallback(() => {
    recordFinishedRef.current = false;
    // 行動記録 seeds from Goals only (after confirm) — no FE regex scrape
    const fromGoals = goalsActionsForRecord(goals);
    if (fromGoals.length) setDecidedActions(fromGoals);
    setRecordOpen(true);
    setChatError(null);
    void ensureSession().catch((err) => {
      setChatError(err.message || 'セッションの作成に失敗しました');
    });
  }, [goals, setChatError, ensureSession]);

  /**
   * 「行動を記録する」: today may already be recorded elsewhere (home popup, 記録を編集 sheet) —
   * every place saves the same action_checkins/{today} document. Then say so and show what was
   * saved instead of asking the 3 questions again; 「記録を直す」 reopens them (the save overwrites).
   */
  const startRecordInChat = useCallback(
    async ({ force = false } = {}) => {
      setChatMessages((prev) => [...clearLastChoices(prev), { role: 'user', text: force ? '✏️ 記録を直す' : '行動を記録する' }]);
      if (!force) {
        const today = tokyoToday();
        const saved = await fetchActionCheckins({ store, stylist, date: today })
          .then((res) => res?.checkin || null)
          .catch(() => null); // unknown → ask as before
        if (saved) {
          const lines = (saved.entries || []).map((e, i) => `${CIRCLED[i] || i + 1}${formatCheckinEntry(e)}`);
          setChatMessages((prev) => [
            ...prev,
            { role: 'ai', text: `今日 ${dayLabel(today)}（${weekday(today)}）の行動は記録済みです。` },
            ...lines.map((text) => ({ role: 'ai', text })),
            { role: 'ai', text: '直したいときは「記録を直す」を選んでください。', choices: ['✏️ 記録を直す', '📊 現状分析・振り返り', '💡 改善策を相談'] },
          ]);
          setRecordOpen(false);
          return;
        }
      }
      openRecordFlow();
    },
    [store, stylist, setChatMessages, openRecordFlow]
  );

  const onRecordFinished = useCallback(
    ({ ans, reasons, anyNo, actions: finishedActions }) => {
      if (recordFinishedRef.current) return;
      recordFinishedRef.current = true;
      setRecordOpen(false);
      const summaryLines = (finishedActions || []).map((action, i) => {
        const reason =
          ans[i] === 'できなかった' && reasons[i] ? `（理由: ${reasons[i]}）` : '';
        return `${CIRCLED[i] || i + 1}「${action.name}」—— ${ans[i]}${reason}`;
      });
      setChatMessages((prev) => {
        const extra = summaryLines.map((text) => ({ role: 'ai', text }));
        extra.push({
          role: 'ai',
          text: anyNo
            ? '記録ありがとうございます。できなかった行動も、理由がわかれば次に活かせます。'
            : '記録ありがとうございます。今日は積み重ねできましたね。',
          choices: ['📊 現状分析・振り返り', '💡 改善策を相談'],
        });
        return [...prev, ...extra];
      });
      // Background: append to session so history title/preview/messages update
      const persistText = `【行動記録】\n${summaryLines.join('\n')}`;
      void (async () => {
        try {
          const sid = await ensureSession();
          await postChatMessage(sid, persistText);
          onSessionsChanged?.();
        } catch {
          // Local UI + action_checkins already saved; history may lag until next chat turn
        }
      })();
    },
    [setChatMessages, ensureSession, onSessionsChanged]
  );

  // Block sending into a thread bound to someone else — ask the user to pick
  // continue-with-them vs. start-fresh-with-the-current-selection instead.
  // No activeSessionId yet means nothing to collide with (ensureSession binds
  // the new session to the current selection).
  const handleSend = async (text) => {
    const trimmed = (text || chatFailedText || '').trim();
    if (!trimmed || chatSending) return;
    if (recordOpen && memoCapture) {
      // 行動記録 is waiting for a ひと言: this text is the ひと言, not a message to the AI
      memoCapture(trimmed);
      return;
    }
    if (activeSessionId && !sessionMatchesSelection) {
      setMismatchText(trimmed);
      return;
    }
    return doSend(text);
  };

  const doSend = async (text) => {
    const trimmed = (text || chatFailedText || '').trim();
    if (!trimmed || chatSending) return;

    // Stay in THIS chat — do not open a separate AiRecord screen
    if (isRecordIntent(trimmed)) {
      if (!store || !stylist) {
        setChatError('ホームで店舗とスタイリストを選択してから行動を記録してください');
        return;
      }
      void startRecordInChat();
      return;
    }

    if (!store || !stylist) {
      setChatError('ホームで店舗とスタイリストを選択してからAI相談を開始してください');
      return;
    }

    // Map post-record / recovery chips back to review/improve / replace clarify
    let outbound = trimmed;
    let displayText = trimmed;
    let isImproveTurn = false;
    if (trimmed.includes('現状分析') || trimmed.includes('振り返り') || trimmed.includes('当月実績')) {
      outbound = buildFlowPrompt('review', data.actions, data.latestCheckin, goals);
      displayText = trimmed.includes('当月実績') ? '📊 当月実績を見る' : trimmed;
    } else if (
      trimmed.includes('改善策') ||
      trimmed.startsWith('【改善相談】') ||
      trimmed === '💡 改善策を相談'
    ) {
      outbound = buildFlowPrompt('improve', data.actions, data.latestCheckin, goals);
      displayText = '💡 改善策を相談';
      isImproveTurn = true;
      setFlow('improve');
    } else if (trimmed.includes('今日試して記録') || trimmed.includes('行動を記録') || trimmed.includes('記録を直す')) {
      if (!store || !stylist) {
        setChatError('ホームで店舗とスタイリストを選択してから行動を記録してください');
        return;
      }
      void startRecordInChat({ force: trimmed.includes('記録を直す') });
      return;
    } else if (/^[①1]/.test(trimmed) && (trimmed.includes('変え') || trimmed.includes('差し替'))) {
      outbound =
        '①の行動を差し替えたいです。新しい具体案を1つ出し、ACTION_REPLACE で保存できる形にしてください。';
    } else if (/^[②2]/.test(trimmed) && (trimmed.includes('変え') || trimmed.includes('差し替'))) {
      outbound =
        '②の行動を差し替えたいです。新しい具体案を1つ出し、ACTION_REPLACE で保存できる形にしてください。';
    } else if (/^[③3]/.test(trimmed) && (trimmed.includes('変え') || trimmed.includes('差し替'))) {
      outbound =
        '③の行動を差し替えたいです。新しい具体案を1つ出し、ACTION_REPLACE で保存できる形にしてください。';
    }

    setChatSending(true);
    setChatError(null);
    setChatFailedText(null);
    setRecordOpen(false);
    setChatMessages((prev) => [...clearLastChoices(prev), { role: 'user', text: displayText }]);

    // Confirm → write Goals from session pending_proposal (before AI ack stream).
    // Chip text picks the kind; server rejects if it mismatches the pending slot.
    const confirmKind = isReplaceConfirm(trimmed)
      ? 'replace'
      : isGoalsConfirm(trimmed)
        ? 'goals'
        : isProposalConfirm(trimmed)
          ? 'full'
          : null;
    if (confirmKind) {
      try {
        const confirmRes = await confirmActionsFromSession(confirmKind);
        if (confirmRes?.submission) {
          // Managers decide their own plan: the server returns it already approved.
          const decided = confirmRes.submission.status === 'approved';
          setChatMessages((prev) => [
            ...prev,
            {
              role: 'ai',
              text: decided
                ? '✅ 行動プランを確定しました。毎日の行動プランに反映されています。'
                : '📨 行動プランをマネージャーに送りました（確認中）。承認されると「確定」になり、毎日の行動プランに反映されます。',
            },
          ]);
        }
      } catch (err) {
        setChatSending(false);
        // full chip after a free-form "1件だけ" turn often means no ACTIONS line /
        // pending was never written — guide user to the right next step
        const raw = err.message || '保存に失敗しました';
        setChatError(raw);
        setChatFailedText(trimmed);
        if (confirmKind === 'full' || confirmKind === 'replace') {
          setChatMessages((prev) => [
            ...prev,
            {
              role: 'ai',
              text:
                '保存できる提案形式になっていませんでした。次のどちらかを選んでください。',
              choices: [
                '💡 改善策を相談',
                '①を変えたい',
                '②を変えたい',
                '③を変えたい',
                '別の案がほしい',
              ],
            },
          ]);
        }
        return;
      }
    }

    const controller = new AbortController();
    abortRef.current = controller;

    const streamTimer = setTimeout(() => controller.abort(), STREAM_TIMEOUT_MS);
    try {
      let sid = activeSessionIdRef.current;
      if (!sid) {
        sid = await ensureSession();
      }

      setChatMessages((prev) => [...prev, { role: 'ai', text: '', streaming: true }]);

      // Refresh session KPI from current home BQ snapshot + current plan titles
      // (model needs exact titles to emit a valid ACTION_REPLACE line)
      // Only when the on-screen selection still matches the session's own
      // store/stylist — otherwise the server keeps the dossier it already has
      // rather than being handed another person's numbers.
      const freshKpi = sessionMatchesSelection
        ? mergePlanIntoKpiSummary(kpiSummary, buildPlanLine(goalsRef.current))
        : null;
      const res = await postChatMessageStream(sid, outbound, {
        signal: controller.signal,
        kpi_summary: freshKpi || undefined,
        onDelta: (chunk) => {
          setChatMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last?.role === 'ai' && last.streaming) {
              next[next.length - 1] = { role: 'ai', text: (last.text || '') + chunk, streaming: true };
            }
            return next;
          });
        },
        // The server is running the POS lookup tool: show the wait so the person knows it is real.
        onStatus: (text) => {
          setChatMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last?.role === 'ai' && last.streaming) {
              next[next.length - 1] = { ...last, status: text };
            }
            return next;
          });
        },
      });

      if (res.identity_changed) {
        // 呼び名 saved from chat: refresh the profile the app holds so 設定 and greetings show it now
        fetchMyIdentity()
          .then((p) => p && typeof p === 'object' && setMyProfile(p))
          .catch(() => {});
      }
      const reply = res.assistant_message?.content || '';
      const pending = res.assistant_message?.pending_proposal || res.pending_proposal || null;
      const practiceRaw = res.assistant_message?.practice_memo || pending?.practice_memo || null;
      const pendingActions =
        pending?.kind === 'full' && Array.isArray(pending.actions) ? pending.actions : null;
      const practiceMemo =
        isImproveTurn || practiceRaw || pendingActions
          ? buildPracticeMemoCard(practiceRaw, pendingActions, {
              nextMonthLabel: data.monthSnap?.nextMonth || '',
            })
          : null;
      let choices = res.assistant_message?.choices || [];
      if (isImproveTurn || practiceMemo) {
        choices = mergeImproveChoices(choices);
      }
      setChatMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        const msg = { role: 'ai', text: reply, choices, practiceMemo: practiceMemo || undefined, rateable: !!reply };
        if (last?.role === 'ai') {
          next[next.length - 1] = msg;
        } else {
          next.push(msg);
        }
        return next;
      });
      onSessionsChanged?.();
    } catch (err) {
      if (err.name === 'AbortError') {
        setChatError('応答がタイムアウトしました。再送信してください');
        setChatFailedText(trimmed);
        setChatMessages((prev) => prev.filter((m) => !(m.role === 'ai' && m.streaming)));
        return;
      }
      setChatError(err.message || '送信に失敗しました');
      setChatFailedText(trimmed);
      setChatMessages((prev) => prev.filter((m) => !(m.role === 'ai' && m.streaming)));
    } finally {
      clearTimeout(streamTimer);
      setChatSending(false);
      abortRef.current = null;
    }
  };

  // After startNewChat() clears the session (in AiStack), send the held message
  // into the fresh thread that's now bound to the current selection.
  useEffect(() => {
    if (activeSessionId == null && pendingAfterNewChatRef.current) {
      const text = pendingAfterNewChatRef.current;
      pendingAfterNewChatRef.current = null;
      doSend(text);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires once per startNewChat, not per doSend identity
  }, [activeSessionId]);

  const continueMismatchedSession = () => {
    const text = mismatchText;
    setMismatchText(null);
    doSend(text);
  };

  const startNewChatForMismatch = () => {
    pendingAfterNewChatRef.current = mismatchText;
    setMismatchText(null);
    startNewChat?.();
  };

  // Greeting + three cards stay at top of the same thread (after picks / during record)
  const selectionMissing = !store || !stylist;
  // Canned bubbles use the same 呼び名 + 敬称 as the AI when the chat is about the person themself.
  const posName = stylist || data.stylistName;
  const { name: greetName, suffix: greetSuffix } = greetingFor(myProfile, posName);
  const fromGoals = goalsActionsForRecord(goals);
  const fallbackActions = fromGoals.length
    ? fromGoals
    : (data.actions || [])
        .filter((a) => a?.name && !isMetaActionLabel(a.name))
        .slice(0, 3);
  const decidedClean = (decidedActions || []).filter((a) => a?.name && !isMetaActionLabel(a.name));
  const actions = decidedClean.length ? decidedClean : fallbackActions;
  const hasThreadActivity = Boolean(activeSessionId || chatMessages.length > 0 || recordOpen);

  const improveMonthLabel = data.monthSnap?.label || month || '';

  // Boot dedicated improve route (AiStack case 'improve') once into this flow
  useEffect(() => {
    if (initialFlow !== 'improve' || improveBootRef.current) return;
    if (!store || !stylist) return;
    improveBootRef.current = true;
    setFlow('improve');
    void handleSend('💡 改善策を相談');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- boot once when route opens
  }, [initialFlow, store, stylist]);

  return (
    <div className="ai-screen">
      <ChatHeader
        title={flow === 'improve' ? '改善策を相談' : 'AI相談'}
        onHistory={onHistory}
        onNewChat={hasThreadActivity && startNewChat ? startNewChat : undefined}
        settingsPath={settingsPath}
      />
      {flow === 'improve' && (
        <ChatBanner icon="💡" title={`${improveMonthLabel || '今月'}の改善相談`} />
      )}
      <div className="chat-scroll fade">
        <DateDivider label={data.dateDividerLabel} />
        {selectionMissing && (
          <div className="databanner">
            ホームで店舗とスタイリストを選択すると、その人としてAI相談できます
            {store ? `（店舗: ${store}）` : ''}
            {stylist ? `（スタイリスト: ${stylist}）` : ''}
          </div>
        )}
        {!selectionMissing && flow !== 'improve' && (
          <div className="databanner" style={{ opacity: 0.85 }}>
            {`${store} / ${stylist}${month ? ` / ${month}` : ''} として相談中`}
          </div>
        )}
        {!selectionMissing && dossierMeta?.chip && (
          <div className="databanner" style={{ opacity: 0.75, fontSize: 12 }}>
            {dossierMeta.chip}
          </div>
        )}
        {flow !== 'improve' && (
          <>
            <AiBubble>{`${greetName}${greetSuffix}、今日はどれから始めますか？`}</AiBubble>
            <ChatMenu
              onNavigate={(route) => {
                if (route === 'record') {
                  if (!store || !stylist) {
                    setChatError('ホームで店舗とスタイリストを選択してから行動を記録してください');
                    return;
                  }
                  void startRecordInChat();
                  return;
                }
                if (route === 'improve') {
                  setFlow('improve');
                  handleSend('💡 改善策を相談');
                  return;
                }
                const prompt = buildFlowPrompt(route, data.actions, data.latestCheckin, goals);
                if (prompt) handleSend(prompt);
              }}
              items={[
                { icon: '📊', title: '現状分析・振り返り', desc: '今月の数字をAIと一緒に確認', route: 'review' },
                { icon: '📝', title: '行動を記録する', desc: '今日の3つの行動をチェック', route: 'record' },
                { icon: '💡', title: '改善策を相談', desc: '次の一手をAIがご提案', route: 'improve' },
              ]}
            />
          </>
        )}
        {flow === 'improve' && chatMessages.length === 0 && (
          <AiBubble>{`${greetName}${greetSuffix}、改善の打ち手を一緒に組み立てましょう。`}</AiBubble>
        )}
        {chatMessages.map((m, i) =>
          m.role === 'user' ? (
            <UserBubble key={i} text={m.text} />
          ) : (
            <React.Fragment key={i}>
              <AiBubble text={m.text} streaming={m.streaming} />
              {m.streaming && m.status && (
                <div className="set-note" aria-live="polite">{m.status}</div>
              )}
              {/* 👍 / 👎 only on replies that came from the server — not on texts composed here */}
              {!m.streaming && m.rateable && (
                <ReplyFeedback
                  key={`${activeSessionIdRef.current}-${i}`}
                  sessionId={activeSessionIdRef.current}
                  reply={m.text}
                  question={chatMessages.slice(0, i).reverse().find((p) => p.role === 'user')?.text || ''}
                />
              )}
              {!m.streaming && m.practiceMemo && <PracticeMemoCard memo={m.practiceMemo} />}
              {/* 振り返り shows the month card only — the action plan belongs to 改善相談,
                  which is the flow that actually produces those three actions. */}
              {!m.streaming && flowOf(chatMessages, i) === 'review' && <MonthVsTargetCard data={data} />}
              {!m.streaming && flowOf(chatMessages, i) === 'improve' && (
                <ActionPlanCard actions={data.actions} recordedDays={data.recordedDays} />
              )}
              {i === chatMessages.length - 1 && !m.streaming && !recordOpen && (
                <ChoiceBar choices={m.choices} onPick={handleSend} disabled={chatSending || selectionMissing} />
              )}
            </React.Fragment>
          )
        )}
        {recordOpen && actions.length > 0 && (
          <InlineRecordFlow
            key={actions.map((a) => a.name).join('|')}
            stylistName={posName}
            shortDate={data.dailyReview?.shortDate || ''}
            actions={actions}
            reasonOpts={data.dailyReview?.reasons || []}
            store={store}
            stylist={stylist}
            onFinished={onRecordFinished}
            onMemoWaiting={onMemoWaiting}
          />
        )}
        {recordOpen && actions.length === 0 && (
          <div className="databanner">行動プランが未設定です。設定から行動を追加してください。</div>
        )}
        {healthWarn && <div className="databanner">{healthWarn}</div>}
        {chatError && (
          <div className="databanner">
            {chatError}
            {chatFailedText && (
              <button type="button" className="chip primary" style={{ marginTop: 8 }} onClick={() => handleSend(chatFailedText)}>
                再送信
              </button>
            )}
          </div>
        )}
      </div>
      {mismatchText && (
        <div className="databanner">
          {`この会話は${activeSessionStylist || posName}さんについてです。${stylist}さんとして新しく始めますか？`}
          <div className="chips" style={{ marginTop: 8 }}>
            <button type="button" className="chip" onClick={continueMismatchedSession}>
              {`続ける（${activeSessionStylist || posName}さんのまま）`}
            </button>
            <button type="button" className="chip primary" onClick={startNewChatForMismatch}>
              {`新しく始める（${stylist}さんとして）`}
            </button>
          </div>
        </div>
      )}
      <ChatInput
        placeholder={
          selectionMissing
            ? '先にホームで店舗・スタイリストを選択…'
            : chatSending
              ? '送信中…'
              : recordOpen && memoCapture
                ? 'ひと言を入力（任意）…'
                : flow === 'improve'
                ? '相談内容を入力…'
                : '話したいことを入力…'
        }
        onSend={handleSend}
        disabled={chatSending || selectionMissing || Boolean(mismatchText)}
      />
    </div>
  );
}
